import { ColorSchemeNamesInput } from '@razorpay/blade/tokens';
import React, { createContext, useCallback, useContext } from 'react';

type ThemeCallback = (theme: ColorSchemeNamesInput) => void;
class WidgetThemeStore {
  private static instance: WidgetThemeStore;
  private callbacks: Set<ThemeCallback>;
  private currentTheme: ColorSchemeNamesInput;

  private constructor(theme?: ColorSchemeNamesInput) {
    this.callbacks = new Set();
    this.currentTheme = theme || 'dark';
  }

  public static getInstance(theme?: ColorSchemeNamesInput): WidgetThemeStore {
    if (!WidgetThemeStore.instance) {
      WidgetThemeStore.instance = new WidgetThemeStore(theme);
    }
    return WidgetThemeStore.instance;
  }

  public registerCallback(callback: ThemeCallback): () => void {
    this.callbacks.add(callback);
    callback(this.currentTheme);
    return () => {
      this.callbacks.delete(callback);
    };
  }

  public notifyThemeChange(newTheme: ColorSchemeNamesInput): void {
    this.currentTheme = newTheme;
    this.callbacks.forEach((callback) => callback(newTheme));
  }

  public getCurrentTheme(): ColorSchemeNamesInput {
    return this.currentTheme;
  }
}

interface WidgetThemeContextType {
  theme: ColorSchemeNamesInput;
  registerThemeCallback: (callback: ThemeCallback) => () => void;
  notifyThemeChange: (newTheme: ColorSchemeNamesInput) => void;
}

interface WidgetThemeContextProviderProps {
  children: React.ReactNode;
  initialTheme?: ColorSchemeNamesInput;
}

const WidgetThemeContext = createContext<WidgetThemeContextType | null>(null);

export const WidgetThemeContextProvider: React.FC<WidgetThemeContextProviderProps> = ({
  children,
  initialTheme,
}) => {
  const store = WidgetThemeStore.getInstance(initialTheme);

  const value: WidgetThemeContextType = {
    theme: store.getCurrentTheme(),
    registerThemeCallback: useCallback((callback: ThemeCallback) => {
      return store.registerCallback(callback);
    }, []),
    notifyThemeChange: useCallback((newTheme: ColorSchemeNamesInput) => {
      store.notifyThemeChange(newTheme);
    }, []),
  };

  return <WidgetThemeContext.Provider value={value}>{children}</WidgetThemeContext.Provider>;
};

export const useWidgetThemeContext = (): WidgetThemeContextType | null => {
  const context = useContext(WidgetThemeContext);
  return context;
};
